import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { useAuth } from '../../../useAuth';
import { db } from '../../../firebase';
import './rewardsActivity.css';

const AUDIT_LIMIT = 200;

const FILTERS = [
  { id: 'claims', label: 'Claims' },
  { id: 'all', label: 'All activity' },
  { id: 'transfers', label: 'Transfers' },
  { id: 'refunds', label: 'Refunds & returns' },
  { id: 'tiers', label: 'Tiers' },
];

const FILTER_EVENTS = {
  claims: new Set(['reward_redeemed', 'reward_redeem_failed']),
  transfers: new Set(['points_transferred']),
  refunds: new Set(['rewards_points_clawed_refund', 'shopify_return_processed']),
  tiers: new Set(['tier_unlocked', 'tier_tag_repaired']),
};

function rewardFromOptionId(optionId) {
  const id = String(optionId || '');
  if (id === 'pct-10') return { label: '10% off', points: 1500 };
  if (id === 'pct-15') return { label: '15% off', points: 3000 };
  if (id === 'pct-20') return { label: '20% off', points: 5000 };
  if (id === 'pct-25') return { label: '25% off', points: 5000 };
  if (id === 'birthday-5') return { label: 'Birthday 5% off', points: 0 };
  const fixed = /^off-(\d+)$/.exec(id);
  if (fixed) {
    const amount = Number(fixed[1]);
    return { label: `$${amount} off`, points: (amount / 10) * 300 };
  }
  return { label: id || 'a reward', points: null };
}

function formatWhen(ts) {
  if (!ts) return '—';
  try {
    const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatPhone(phone) {
  const d = String(phone || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return phone || '—';
}

function phone10(value) {
  const d = String(value || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return d.length === 10 ? d : '';
}

function titleCase(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function sourceLabel(source) {
  if (source === 'pos') return 'In store';
  if (source === 'storefront') return 'Online';
  return source ? titleCase(source) : '';
}

function customerLabel(phone, names, fallbackName) {
  const key = phone10(phone);
  const stored = (key && names[key]) || fallbackName || '';
  return stored.trim() || formatPhone(key || phone);
}

function presentEntry(entry, names) {
  const details = entry.details || {};
  const phone = phone10(details.phone10);
  const name = customerLabel(phone, names);
  const reward = rewardFromOptionId(details.optionId);
  const points = Number(details.pointsCost ?? reward.points);

  switch (entry.event) {
    case 'reward_redeemed':
      return {
        tone: 'claim',
        badge: 'Claimed',
        name,
        phone,
        headline: `Claimed ${reward.label}`,
        detail: [
          Number.isFinite(points) ? `${points.toLocaleString()} points` : '',
          sourceLabel(details.source),
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'reward_redeem_failed':
      return {
        tone: 'failed',
        badge: 'Claim failed',
        name,
        phone,
        headline: `Could not claim ${reward.label}`,
        detail: details.reason || 'Points were not used.',
      };
    case 'points_transferred': {
      const fromPhone = phone10(details.fromPhone);
      const toPhone = phone10(details.toPhone);
      const amount = Number(details.amount);
      return {
        tone: 'transfer',
        badge: 'Transfer',
        name: customerLabel(fromPhone, names, details.fromName),
        phone: fromPhone,
        secondaryName: customerLabel(toPhone, names, details.toName),
        secondaryPhone: toPhone,
        headline: Number.isFinite(amount)
          ? `Sent ${amount.toLocaleString()} points`
          : 'Points transferred',
        detail: 'To another customer',
      };
    }
    case 'rewards_points_clawed_refund': {
      const clawed = Number(details.pointsClawed);
      const refund = Number(details.refundAmount);
      return {
        tone: 'refund',
        badge: 'Refund',
        name,
        phone,
        headline: Number.isFinite(clawed)
          ? `${clawed.toLocaleString()} points removed`
          : 'Points removed after a refund',
        detail: [
          details.orderDocId ? `Order ${details.orderDocId}` : '',
          Number.isFinite(refund) ? `$${refund.toFixed(2)} refunded` : '',
        ]
          .filter(Boolean)
          .join(' · '),
      };
    }
    case 'shopify_return_processed': {
      const delta = Number(details.pointsDelta);
      return {
        tone: 'refund',
        badge: 'Return',
        name,
        phone,
        headline: Number.isFinite(delta) && delta !== 0
          ? `${delta > 0 ? '+' : ''}${delta.toLocaleString()} points`
          : 'Return recorded',
        detail: [
          details.orderDocId ? `Order ${details.orderDocId}` : '',
          details.hasExchange ? 'Includes an exchange' : '',
        ]
          .filter(Boolean)
          .join(' · '),
      };
    }
    case 'tier_unlocked':
      return {
        tone: 'tier',
        badge: 'Tier',
        name,
        phone,
        headline: `Reached ${titleCase(details.nextTier) || 'a new tier'}`,
        detail: details.previousTier
          ? `Was ${titleCase(details.previousTier)}`
          : '',
      };
    case 'tier_tag_repaired':
      return {
        tone: 'tier',
        badge: 'Tier sync',
        name,
        phone,
        headline: `Tier set to ${titleCase(details.tier) || 'current tier'}`,
        detail: details.expectedTag ? `Shopify tag ${details.expectedTag}` : '',
      };
    case 'otp_verified':
      return {
        tone: 'signin',
        badge: 'Signed in',
        name,
        phone,
        headline: 'Opened rewards',
        detail: '',
      };
    case 'otp_sent':
      return {
        tone: 'signin',
        badge: 'Code sent',
        name,
        phone,
        headline: 'Requested a sign-in code',
        detail: '',
      };
    default:
      return {
        tone: 'other',
        badge: entry.event.replace(/_/g, ' '),
        name,
        phone,
        headline: entry.event.replace(/_/g, ' '),
        detail: '',
      };
  }
}

function searchBlob(card) {
  return [
    card.name,
    card.phone,
    card.secondaryName,
    card.secondaryPhone,
    card.headline,
    card.detail,
    card.badge,
    formatPhone(card.phone),
    formatPhone(card.secondaryPhone),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function RewardsActivity() {
  const { user, claims, loading: authLoading } = useAuth();
  const [entries, setEntries] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('claims');

  const isAdmin = claims.admin === true;

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user || !isAdmin) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(db, 'RewardsAuditLog'),
      orderBy('createdAt', 'desc'),
      limit(AUDIT_LIMIT)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError('');
      },
      (err) => {
        console.error('RewardsAuditLog snapshot error:', err);
        setLoading(false);
        setError('Could not load rewards activity. Refresh and try again.');
      }
    );

    return () => unsub();
  }, [authLoading, user, isAdmin]);

  useEffect(() => {
    const phones = new Set();
    entries.forEach((entry) => {
      const details = entry.details || {};
      [details.phone10, details.fromPhone, details.toPhone].forEach((value) => {
        const key = phone10(value);
        if (key) phones.add(key);
      });
    });

    const missing = [...phones].filter((phone) => names[phone] === undefined);
    if (missing.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      missing.map(async (phone) => {
        try {
          const snap = await getDoc(doc(db, 'Customers', phone));
          const name = snap.exists() ? String(snap.data()?.name || '').trim() : '';
          return [phone, name];
        } catch {
          return [phone, ''];
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      setNames((prev) => {
        const next = { ...prev };
        rows.forEach(([phone, name]) => {
          next[phone] = name;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [entries, names]);

  const cards = useMemo(() => {
    const allowed = FILTER_EVENTS[filter];
    const term = search.trim().toLowerCase();
    const digits = search.replace(/\D/g, '');

    return entries
      .filter((entry) => !allowed || allowed.has(entry.event))
      .map((entry) => ({
        id: entry.id,
        when: formatWhen(entry.createdAt),
        ...presentEntry(entry, names),
      }))
      .filter((card) => {
        if (!term && !digits) return true;
        const blob = searchBlob(card);
        if (term && blob.includes(term)) return true;
        if (digits && blob.replace(/\D/g, '').includes(digits)) return true;
        return false;
      });
  }, [entries, names, filter, search]);

  if (authLoading || loading) {
    return (
      <div className="rewards-activity-page">
        <div className="rewards-activity-header">
          <h1>Rewards</h1>
        </div>
        <p className="rewards-activity-status">Loading…</p>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="rewards-activity-page">
        <div className="rewards-activity-header">
          <h1>Rewards</h1>
        </div>
        <p className="rewards-activity-error">You need admin access to view this page.</p>
      </div>
    );
  }

  return (
    <div className="rewards-activity-page">
      <div className="rewards-activity-header">
        <h1>Rewards</h1>
        <p className="rewards-activity-subtitle">
          Recent reward claims and account activity. Newest first (last {AUDIT_LIMIT}).
        </p>
      </div>

      <div className="rewards-activity-toolbar">
        <input
          type="search"
          className="rewards-activity-search"
          placeholder="Search name, phone, or reward"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search rewards activity"
        />
        <div className="rewards-activity-filters" role="tablist" aria-label="Activity type">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={filter === item.id}
              className={`rewards-activity-filter${filter === item.id ? ' is-active' : ''}`}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="rewards-activity-error">{error}</p> : null}

      {cards.length === 0 ? (
        <p className="rewards-activity-empty">
          {entries.length === 0
            ? 'No rewards activity yet.'
            : 'Nothing matches that search.'}
        </p>
      ) : (
        <div className="rewards-activity-grid">
          {cards.map((card) => (
            <article key={card.id} className={`rewards-activity-card tone-${card.tone}`}>
              <div className="rewards-activity-card-top">
                <span className="rewards-activity-badge">{card.badge}</span>
                <time className="rewards-activity-when">{card.when}</time>
              </div>
              <h2 className="rewards-activity-name">{card.name}</h2>
              {card.phone ? (
                <p className="rewards-activity-phone">{formatPhone(card.phone)}</p>
              ) : null}
              {card.secondaryName ? (
                <p className="rewards-activity-transfer">
                  To {card.secondaryName}
                  {card.secondaryPhone ? ` · ${formatPhone(card.secondaryPhone)}` : ''}
                </p>
              ) : null}
              <p className="rewards-activity-headline">{card.headline}</p>
              {card.detail ? <p className="rewards-activity-detail">{card.detail}</p> : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default RewardsActivity;
